import { Box, Button, Divider, Grid, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useGlobalSnackbar } from "../../../context/SnackbarContext";
import { TextFieldComponent } from "../../common/TextFieldComponent";
import { ContactInformationFields } from "../../shared/components/forms/contactInformation/ContactInformationFields";
import { colors as color } from "../../ui/colors";
import { SASButtonStyles } from "../../ui/styles/inputStyles";
import { useUpdateWorkforce } from "../WorkforceInfoView/components/hooks/useWorkForce";

interface WorkforceFormProps {
  workforce: any;
  workforceUid: string;
  onUpdate?: () => void | Promise<void>;
  sx?: any;
}

export const WorkforceForm: React.FC<WorkforceFormProps> = ({ workforce, workforceUid, onUpdate, sx = {} }) => {
  const { showSnackbar } = useGlobalSnackbar();
  const { mutate, isPending } = useUpdateWorkforce();

  console.log("workforce data: ", workforce);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      alias: "",
      hourlyRate: "",
      emails: [{ email: "", tag: "other", isDefault: true }],
      phones: [{ countryCode: "US", dialCode: "+1", phone: "", phoneNumber: "" }],
    },
  });

  const [contactState, setContactState] = useState<{ emails: any[]; phones: any[]; isValid: boolean }>({
    emails: [],
    phones: [],
    isValid: true,
  });

  const handleContactInfoChange = useCallback((state: any) => {
    setContactState(state);
  }, []);
  const [showContactErrors, setShowContactErrors] = useState(false);

  const isInitialized = useRef(false);

  useEffect(() => {
    isInitialized.current = false;
  }, [workforce?.uid, workforce?.updatedAt]);

  useEffect(() => {
    if (isInitialized.current) return;
    if (!workforce || Object.keys(workforce).length === 0) {
      isInitialized.current = true;
      return;
    }

    let emails: any[] = [{ email: "", tag: "other", isDefault: true }];
    if (Array.isArray(workforce.emails) && workforce.emails.length > 0) {
      emails = workforce.emails.map((e: any) => {
        if (typeof e === "string") {
          return { email: e, tag: "other", isDefault: false };
        }
        return {
          email: e?.email || e?.value || "",
          tag: e?.tag || "other",
          isDefault: !!e?.isDefault,
        };
      });
    } else if (workforce.email) {
      emails = [{ email: workforce.email, tag: "other", isDefault: true }];
    }

    let phones: any[] = [{ countryCode: "US", dialCode: "+1", phone: "", phoneNumber: "", tag: "other", isDefault: true }];
    if (Array.isArray(workforce.phoneNumbers) && workforce.phoneNumbers.length > 0) {
      phones = workforce.phoneNumbers.map((p: any) => ({
        countryCode: p.countryCode || "US",
        dialCode: p.dialCode || "+1",
        phone: p.phone || "",
        phoneNumber: p.phoneNumber || (p.dialCode || "+1") + (p.phone || ""),
        tag: p.tag || "other",
        isDefault: !!p.isDefault,
      }));
    } else if (workforce.phone) {
      phones = [
        {
          countryCode: workforce.countryCode || "US",
          dialCode: workforce.dialCode || "+1",
          phone: workforce.phone,
          phoneNumber: (workforce.dialCode || "+1") + (workforce.phone || ""),
          tag: "other",
          isDefault: true,
        },
      ];
    }

    reset({
      firstName: workforce.firstName || "",
      lastName: workforce.lastName || "",
      alias: workforce.alias || "",
      hourlyRate: workforce.hourlyRate ?? workforce.ratePerHour ?? "",
      emails,
      phones,
    });
    isInitialized.current = true;
  }, [workforce, reset]);

  const onSubmit = async (data: any) => {
    if (!contactState.isValid) {
      setShowContactErrors(true);
      return;
    }

    // Map emails
    const mappedEmails = Array.isArray(contactState.emails)
      ? contactState.emails
          .map((e: any) => {
            if (typeof e === "string") return { email: e, tag: "other", isDefault: false };
            return {
              email: e?.email || e?.value || "",
              tag: e?.tag || "other",
              isDefault: e?.isDefault || undefined,
            };
          })
          .filter((e: any) => e.email)
      : [];

    // Map phone numbers
    const mappedPhoneNumbers = Array.isArray(contactState.phones)
      ? contactState.phones
          .map((p: any) => {
            const cleanPhone = typeof p.phone === "string" ? p.phone.replace(/\D/g, "") : "";
            const dialCode = p.dialCode || "+1";
            return {
              countryCode: p.countryCode || "US",
              dialCode,
              phone: cleanPhone,
              phoneNumber: dialCode + cleanPhone,
              tag: p.tag || undefined,
              isDefault: p.isDefault || undefined,
            };
          })
          .filter((p: any) => p.phone)
      : [];

    const payload: any = {
      firstName: data.firstName,
      lastName: data.lastName,
      alias: data.alias,
      hourlyRate: data.hourlyRate !== "" && data.hourlyRate !== null && data.hourlyRate !== undefined ? Number(data.hourlyRate) : null,
      ratePerHour: data.hourlyRate !== "" && data.hourlyRate !== null && data.hourlyRate !== undefined ? Number(data.hourlyRate) : null,
      emails: mappedEmails,
      phoneNumbers: mappedPhoneNumbers,
    };

    mutate(
      { uid: workforceUid, data: payload },
      {
        onSuccess: () => {
          showSnackbar("Workforce member updated successfully", "success");
          onUpdate?.();
        },
        onError: (err: any) => {
          const msg = err?.response?.data?.message || err?.message || "Failed to update workforce member";
          showSnackbar(msg, "error");
        },
      },
    );
  };

  const REGEX_LETTERS_ONLY = /^[A-Za-zÀ-ÿ\s]+$/;

  const SectionTitle = ({ children, icon }: { children: React.ReactNode; icon?: boolean }) => (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        {icon && (
          <Box
            sx={{
              width: 4,
              height: 24,
              bgcolor: color.orange?.base || "#FF6B35",
              borderRadius: 2,
              mr: 2,
            }}
          />
        )}
        <Typography variant="h6" sx={{ fontWeight: 600, color: color.gray?.[800] || "var(--fx-surface-text, #0F172A)", letterSpacing: 0.5 }}>
          {children}
        </Typography>
      </Box>
      <Divider sx={{ borderColor: color.gray?.[200] || "var(--fx-surface-border, #E2E8F0)", opacity: 0.8 }} />
    </Box>
  );

  return (
    <Box
      id="workforce-form-general-info"
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        width: "100%",
        gap: 4,
        py: 2,
        ...sx,
      }}
    >
      {/* Personal information */}
      <Box sx={{ width: "100%" }}>
        <SectionTitle icon>Workforce Information</SectionTitle>
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <TextFieldComponent
              name="firstName"
              errors={errors}
              register={register("firstName", {
                required: { value: true, message: "First name is required" },
                minLength: { value: 2, message: "Minimum of two characters" },
                pattern: { value: REGEX_LETTERS_ONLY, message: "Only letters are allowed" },
              })}
              label="First Name"
              inputLabel="First Name"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextFieldComponent
              name="lastName"
              errors={errors}
              register={register("lastName", {
                minLength: { value: 2, message: "Minimum of two characters" },
                pattern: { value: REGEX_LETTERS_ONLY, message: "Only letters are allowed" },
              })}
              label="Last Name"
              inputLabel="Last Name"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextFieldComponent
              name="alias"
              errors={errors}
              register={register("alias", {
                minLength: { value: 2, message: "Minimum of two characters" },
              })}
              label="Alias"
              inputLabel="Alias"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextFieldComponent
              name="hourlyRate"
              type="number"
              errors={errors}
              register={register("hourlyRate", {
                min: { value: 0, message: "Hourly rate cannot be negative" },
              })}
              label="Hourly Rate"
              inputLabel="Hourly Rate"
            />
          </Grid>
        </Grid>
      </Box>

      {/* Contact information — emails & phones */}
      <ContactInformationFields
        key={isInitialized.current ? workforceUid || "initialized" : "loading"}
        initialEmails={getValues("emails")}
        initialPhones={getValues("phones")}
        onChange={handleContactInfoChange}
        requireEither={true}
        showErrors={showContactErrors}
      />

      {/* Actions */}
      <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 2, width: "100%" }}>
        <Button type="submit" disabled={isPending} sx={{ ...SASButtonStyles }}>
          {isPending ? "Saving…" : "Update"}
        </Button>
      </Box>
    </Box>
  );
};
